import type { ModelSourceView, PlatformCatalog } from "./modelSupply";
import { ApiError, failureOf, type HubAgent, type LocalClientInfo } from "../lib/api";
import { connectionSession, localWorkHref } from "../connections/navigation";

export interface Account {
  role?: "owner" | "admin";
  id: string;
  name: string;
}
/** The address on an account. `note` says why a mail did not go, when it did not. */
export interface EmailState {
  email: string | null;
  pending: string | null;
  mail_ready: boolean;
  note?: string | null;
}
/** A directory row on the Hub's own pages, where pairing is always answered. */
export interface AgentInstance extends HubAgent {
  paired: boolean;
  created_at: number;
  device?: { id: string; name: string; os: string; architecture: string; environment: string;
    client_version: string; state: string; last_seen: number } | null;
}
export interface LocalClient extends LocalClientInfo { agent_count: number }
export interface ClientAuthorization {
  id: string;
  client_id: string;
  device: { name: string; os: string; architecture: string };
  workspace: string;
  status: "pending" | "approved" | "complete";
  expires_in: number;
  recover: boolean;
  imports: { id: string; name: string; keep_stopped: boolean }[];
}

export function clientErrorText(error: unknown): string {
  const code = error instanceof ApiError ? error.code : undefined;
  const words: Record<string, string> = {
    client_credentials: "客户端连接已失效，请在客户端重新连接。",
    client_owner_mismatch: "请登录这台客户端原来连接的账号。",
    client_authorization_expired: "本次连接已过期，请重新连接客户端。",
    client_in_use: "这台电脑的原连接仍在线，请先在原客户端断开。",
    client_import_changed: "已有 Agent 清单已变化，请在客户端重新连接后核对。",
    client_plan_stale: "连接信息已变化，请在客户端重新打开授权页。",
    client_import_invalid: "无法确认已有 Agent，未允许连接。请回客户端核对。",
    client_or_agent_not_found: "未找到这台电脑或 Agent，请重新选择。",
  };
  if (code && words[code]) return words[code];
  if (error instanceof ApiError && error.status === 401) return "请重新登录后继续。";
  if (error instanceof ApiError && error.status === 403) return "当前账号无法完成这项操作。";
  if (error instanceof ApiError && error.status === 404) return "连接请求已失效，请在客户端重新连接。";
  return "暂时未能完成，请重试。";
}
function clientFailure(error: unknown): never {
  if (error instanceof ApiError && error.code?.startsWith("client_")) {
    const shown = new ApiError("操作未完成", clientErrorText(error), null, error.status);
    shown.code = error.code;
    throw shown;
  }
  throw error;
}

export interface CreatedAgent {
  agent: AgentInstance;
  pair_code?: string;
  pair_command?: string;
}
export type Deployment =
  | { kind: "standalone" }
  | { kind: "hub"; account: Account | null };

async function responseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    throw new ApiError(
      "工作台没有返回可读取的结果",
      "请刷新后重试。",
      null,
      response.status,
    );
  }
  if (!response.ok)
    throw failureOf(response.status, body, { title: "请求未完成", detail: `HTTP ${response.status}`, machine: true });
  return body;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/hub${path}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
    },
  });
  return (await responseBody(response)) as T;
}

/** A missing Hub is supported. A failing Hub is not a standalone deployment. */
export async function readDeployment(
  signal?: AbortSignal,
): Promise<Deployment> {
  const response = await fetch("/api/hub/me", {
    signal,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (response.status === 401) return { kind: "hub", account: null };
  if (response.status === 404) return { kind: "standalone" };
  if (
    response.ok &&
    response.headers.get("content-type")?.includes("text/html")
  )
    return { kind: "standalone" };
  const body = (await responseBody(response)) as { account?: Account };
  if (
    !body.account ||
    typeof body.account.id !== "string" ||
    typeof body.account.name !== "string"
  ) {
    throw new ApiError(
      "账号信息不完整",
      "没有进入工作台。请刷新后重试。",
      null,
      502,
    );
  }
  return { kind: "hub", account: body.account };
}

/** The registration line this deployment is configured to show, or null: no footer, also when the Hub cannot say. */
export type Filing = { text: string; url: string };
export async function readFiling(signal?: AbortSignal): Promise<Filing | null> {
  try {
    const response = await fetch("/api/hub/site", { signal, credentials: "same-origin", headers: { Accept: "application/json" } });
    if (!response.ok || !response.headers.get("content-type")?.includes("json")) return null;
    const filing = ((await response.json()) as { filing?: { text?: unknown; url?: unknown } | null }).filing;
    if (!filing || typeof filing.text !== "string" || !filing.text) return null;
    return { text: filing.text, url: typeof filing.url === "string" ? filing.url : "" };
  } catch {
    return null;
  }
}

/** Read a view through its owning Agent's relay. Observations never wake or keep an idle runtime alive. */
export async function readAgentView<T>(id: string | null, path: string, signal?: AbortSignal, observe = false): Promise<T> {
  const prefix = id ? `/agents/${encodeURIComponent(id)}` : "";
  const response = await fetch(prefix + path, { signal, credentials: "same-origin",
    headers: { Accept: "application/json", ...(observe ? { "X-PA-Runtime-Observe": "1" } : {}) } });
  return (await responseBody(response)) as T;
}

/** Submit this request's answer through its owning Agent's existing endpoint. */
export async function answerAgentApproval(id: string, key: string, decision: string): Promise<unknown> {
  const response = await fetch(`/agents/${encodeURIComponent(id)}/api/approvals/${encodeURIComponent(key)}`, {
    method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ decision }),
  });
  return responseBody(response);
}

export async function sendAgentMessage(id: string, sessionId: string, text: string): Promise<unknown> {
  const response = await fetch(`/agents/${encodeURIComponent(id)}/api/chat/${encodeURIComponent(sessionId)}`, {
    method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ text }),
  });
  return responseBody(response);
}

export const agentDeletionsPath = "/api/hub/agent-deletions";
export const modelSupplyPath = "/api/hub/models";
export const modelSourcePath = (id: string) => `/api/hub/agents/${encodeURIComponent(id)}/model-source`;

export interface LocalRelease {
  release_id: string;
  source_commit: string;
  installer: { version: string; size: number; sha256: string };
}

export const localInstaller = {
  read: async (signal?: AbortSignal): Promise<LocalRelease> => {
    const body = await request<LocalRelease>("/local/release", { signal });
    if (!/^sha256:[a-f0-9]{64}$/.test(body.release_id) || !/^[a-f0-9]{40}$/.test(body.source_commit)
      || !body.installer || !/^\d+\.\d+\.\d+$/.test(body.installer.version)
      || !Number.isSafeInteger(body.installer.size) || body.installer.size <= 0
      || !/^[a-f0-9]{64}$/.test(body.installer.sha256)) {
      throw new ApiError("本机端下载暂不可用", "安装包说明尚未就绪。", "请稍后重新检查，或联系平台维护者。", 503);
    }
    return body;
  },
};

export const hub = {
  clients: async (signal?: AbortSignal): Promise<LocalClient[]> => {
    const body = await request<{ clients: LocalClient[] }>("/local/clients", { signal });
    if (!Array.isArray(body.clients) || !body.clients.every(client => client && typeof client.id === "string"
      && typeof client.name === "string" && typeof client.workspace === "string" && typeof client.online === "boolean"
      && Number.isSafeInteger(client.agent_count) && client.agent_count >= 0)) {
      throw new ApiError("电脑列表未读完整", "请重新读取后再选择。", null, 502);
    }
    return body.clients;
  },
  clientAuthorization: (id: string, signal?: AbortSignal) => request<ClientAuthorization>(
    "/local/clients/authorizations/" + encodeURIComponent(id), { signal }).catch(clientFailure),
  approveClient: (id: string, body: { name?: string } = {}) => request<unknown>(
    "/local/clients/authorizations/" + encodeURIComponent(id) + "/approve",
    { method: "POST", body: JSON.stringify(body) }).catch(clientFailure),

  localAuthorization: (id: string) => request<{ device: { name: string; os: string; architecture: string }; status: string; agent_id?: string }>(
    "/local/authorizations/" + encodeURIComponent(id)),
  approveLocal: (id: string, body: { agent_id: string; name: string; assembly?: string }) =>
    request<{ agent_id: string }>("/local/authorizations/" + encodeURIComponent(id) + "/approve",
      { method: "POST", body: JSON.stringify(body) }),
  /** One kind of service, and where it now comes from; answers with the Agent's sources as they now are. */
  setModelSource: (id: string, kind: string, source: "platform" | "own") => request<ModelSourceView>(`/agents/${encodeURIComponent(id)}/model-source`, { method: "PUT", body: JSON.stringify({ kind, source }) }),
  login: (name: string, password: string) =>
    request<{ account: Account }>("/login", {
      method: "POST",
      body: JSON.stringify({ name, password }),
    }),
  signup: (name: string, password: string, invite: string, email: string) =>
    request<{ account: Account }>("/signup", {
      method: "POST",
      body: JSON.stringify({ name, password, invite, email }),
    }),
  logout: () => request<{ ok: boolean }>("/logout", { method: "POST" }),
  /** A new password, proven with the current one. This browser stays signed in; the others end. */
  changePassword: (current: string, password: string) =>
    request<{ revoked: number }>("/password", {
      method: "POST",
      body: JSON.stringify({ current, password }),
    }),
  /** A reset code an admin issued, spent on a new password. The answer signs this browser in. */
  resetPassword: (name: string, code: string, password: string) =>
    request<{ account: Account }>("/password/reset", {
      method: "POST",
      body: JSON.stringify({ name, code, password }),
    }),
  /** Ask for a reset link by account name or confirmed email. The Hub answers the same for every name. */
  forgotPassword: (who: string) =>
    request<{ sent: true }>("/password/forgot", { method: "POST", body: JSON.stringify({ who }) }),
  /** Whether a reset link still works, and whose password it sets. The token travels in the body, never the address. */
  checkResetLink: (token: string) =>
    request<{ name: string }>("/password/recover/check", { method: "POST", body: JSON.stringify({ token }) }),
  /** A reset link spent on a new password. The answer signs this browser in. */
  recoverPassword: (token: string, password: string) =>
    request<{ account: Account }>("/password/recover", { method: "POST", body: JSON.stringify({ token, password }) }),
  /** Whether this Hub can send a mail at all. Needs no sign-in: the sign-in pages say what the address is for. */
  mailReady: (signal?: AbortSignal) => request<{ ready: boolean }>("/mail", { signal }).then(body => body.ready === true),
  /** The address on this account: the confirmed one, and one still waiting to be confirmed. */
  email: () => request<EmailState>("/email"),
  /** Give the account an address to confirm; the password proves it is the owner asking. */
  setEmail: (email: string, current: string) =>
    request<EmailState>("/email", { method: "POST", body: JSON.stringify({ email, current }) }),
  resendConfirmation: () => request<EmailState>("/email/resend", { method: "POST" }),
  /** A confirmation link opened from a mail. Needs no sign-in. */
  confirmEmail: (token: string) =>
    request<{ confirmed: true; email: string | null }>("/email/confirm", { method: "POST", body: JSON.stringify({ token }) }),
  agents: async (signal?: AbortSignal) => {
    const body = await request<{ agents: AgentInstance[] }>("/agents", {
      signal,
    });
    if (!Array.isArray(body.agents))
      throw new ApiError(
        "Agent 列表没有读完整",
        "请重新读取列表，不要重复创建。",
        null,
        502,
      );
    return body.agents;
  },
  assemblies: () => request<{ model_supply?: PlatformCatalog; default: string; assemblies: { key: string; title: string; description: string }[] }>("/assemblies"),
  create: (name: string, location: "cloud" | "local", assembly?: string, clientId?: string) =>
    request<CreatedAgent>("/agents", {
      method: "POST",
      body: JSON.stringify({ name, location, ...(assembly ? { assembly } : {}), ...(clientId ? { client_id: clientId } : {}) }),
    }).catch(clientFailure),
  bindClient: (id: string, clientId: string, assembly?: string) =>
    request<{ agent: AgentInstance }>("/agents/" + encodeURIComponent(id) + "/client", {
      method: "PUT", body: JSON.stringify({ client_id: clientId, ...(assembly ? { assembly } : {}) }),
    }).catch(clientFailure),
  rename: (id: string, name: string) =>
    request<{ agent: AgentInstance }>("/agents/" + encodeURIComponent(id), {
      method: "PATCH", body: JSON.stringify({ name }),
    }),
  start: (id: string) => request<{ agent: AgentInstance }>("/agents/" + encodeURIComponent(id) + "/start",
    { method: "POST" }).catch(clientFailure),
  stop: (id: string) => request<{ agent: AgentInstance }>("/agents/" + encodeURIComponent(id) + "/stop",
    { method: "POST" }).catch(clientFailure),
  deleteAgent: (id: string, confirm: string) =>
    request<{ deleted: { id: string; name: string; location: string }; cleanup_pending?: boolean }>("/agents/" + encodeURIComponent(id), {
      method: "DELETE", body: JSON.stringify({ confirm }),
    }).catch(clientFailure),
  pairingCode: (id: string) =>
    request<CreatedAgent>(`/agents/${encodeURIComponent(id)}/pairing-code`, {
      method: "POST",
    }),
};

export function agentHref(id: string, hash = "#/today"): string {
  return `/agents/${encodeURIComponent(id)}/${hash}`;
}

/**
 * What an Agent's settings link may carry into the Hub's 设置: which setting,
 * the conversation it came from, and the explicit way back. The rest of an
 * Agent page's address is that page's own state and stays behind.
 */
const SETTINGS_ASKS = ["namespace", "session", "request_id", "request_panel", "scope", "scene"];

/** Whether an Agent page's hash is its settings, which under a Hub open in the Hub's 设置. */
export function agentSettingsPage(hash: string): boolean {
  return ["#/connections", "#/prefs"].includes(hash.split("?")[0]);
}

/** The Hub's 设置 for one Agent, opened at what `from` (an Agent settings hash) asked for. */
export function hubSettingsHref(id: string, from = ""): string {
  return `/#/settings?${new URLSearchParams([["agent", id], ...settingsAsks(from)])}`;
}

/** The Agent-side settings page a Hub 设置 address asks its frame to show, if it asks for one. */
export function agentSettingsRoute(hubHash: string): string | undefined {
  const asks = settingsAsks(hubHash);
  return asks.length ? `#/connections?${new URLSearchParams(asks)}` : undefined;
}

function settingsAsks(hash: string): [string, string][] {
  const params = new URLSearchParams(hash.split("?")[1]);
  // The conversation, read the way the settings page itself reads it.
  params.set("session", connectionSession(hash, ""));
  const back = localWorkHref(params.get("return"));
  if (back) params.set("return", back);
  else params.delete("return");
  return [...SETTINGS_ASKS, "return"].flatMap(key => params.get(key) ? [[key, params.get(key)!] as [string, string]] : []);
}

/** Broadcast only invalidation; the receiving tab rereads the cookie's account. */
export function announceAccountChange() {
  if (typeof BroadcastChannel === "undefined") return;
  const channel = new BroadcastChannel("pa:hub:account");
  channel.postMessage("changed");
  channel.close();
}

/** The hashes that open a sign-in page of their own: they are not a page of an Agent to go back to. */
const AUTH_HASH = /^#\/(?:join\/|forgot|reset\/|confirm-email\/)/;

/**
 * Login may return only to an Agent on this same Hub.
 *
 * The page that was open inside the Agent is the address's own fragment: a browser keeps it across the
 * redirect to the sign-in page (`/?next=/agents/<id>/#/approvals`) and no server ever sees it. `hash` is
 * that fragment, and rides along unless `next` already carries one.
 */
export function loginDestination(search: string, hash = ""): string | null {
  const next = new URLSearchParams(search).get("next");
  if (
    !next ||
    !/^\/agents\/[^/\\?#]+(?:\/|$)/.test(next) ||
    /[\\\r\n]/.test(next)
  )
    return null;
  const url = new URL(next, "https://hub.invalid");
  return url.origin === "https://hub.invalid" &&
    url.pathname.startsWith("/agents/")
    ? `${url.pathname}${url.search}${url.hash || returnHash(hash)}`
    : null;
}
const returnHash = (hash: string) => /^#\/[^\s\\]*$/.test(hash) && !AUTH_HASH.test(hash) ? hash : "";

/**
 * One Chinese word for where an instance stands, for every page that says it.
 *
 * Takes the directory row rather than the Hub's own `AgentInstance`, because an
 * Agent's own shell reads the same row to say why its pages are empty, and two
 * vocabularies for one state is how 已停止 and a spinner ended up on screen at
 * the same moment (D681).
 */
/**
 * What to tell an Owner about an Agent that is not connected, and what they
 * can do about it.
 *
 * Three facts, not one. An Agent that has worked and is away has a desk full
 * of records this Hub cannot read until it comes back; an Agent that has never
 * arrived has none, and saying 「连接恢复后读取最新工作」 about it promises a
 * restoration of something that never existed. A read that failed is the third
 * and belongs to the reading, not to the row -- see `useHubReadings`.
 *
 * A local Agent with no token cannot have connected: nothing was ever minted
 * for it to connect with. Beyond that this is what the Hub wrote down, so on
 * an older Hub, for the minutes between an upgrade and an Agent's next dial,
 * "no record" is said instead of the stronger "never" -- which is exactly what
 * is true of the Hub's own knowledge.
 */
export function connectionNotice(
  agent: HubAgent,
): { text: string; action?: { label: string; href: string } } | undefined {
  if (agent.online) return undefined;
  if (agent.location === "local" && agent.client_id) {
    if (agent.desired_running === false && localRuntimeApplied(agent) && agent.local_runtime?.state === "stopped") return undefined;
    return { text: instanceStatus(agent).label, action: { label: "管理 Agent", href: hubSettingsHref(agent.id) } };
  }
  if (agent.location === "cloud" && agent.desired_running === false) return undefined;
  if (agent.runtime?.state === "sleeping" && wakesOnDemand(agent))
    return { text: "打开后自动唤醒，已有工作会保留。" };
  if (wakesOnDemand(agent) && ["starting", "restarting"].includes(agent.runtime!.state))
    return { text: "正在唤醒，连接后可继续原来的工作。" };
  if (everConnected(agent)) return { text: "连接恢复后读取最新工作" };
  if (agent.location === "local" && !agent.paired)
    return {
      text: "还没有配对。在运行它的那台设备上完成配对，它就会连上来。",
      action: { label: "连接电脑", href: hubSettingsHref(agent.id) },
    };
  if (agent.location === "local")
    return { text: "这里还没有收到过它的连接。在那台设备上启动它，它会自己连上来。" };
  return { text: "这里还没有收到过它的连接。它由这里启动，连上来以后就能看到工作。" };
}

/** Whether this Hub has ever had this Agent on the other end of a socket. */
export function everConnected(agent: HubAgent): boolean {
  return typeof agent.last_connected_at === "number";
}

/** Opening this Agent is enough to wake it; Owner-disabled instances never qualify. */
export function wakesOnDemand(agent: HubAgent | null | undefined): boolean {
  return agent?.location === "cloud" && agent.desired_running === true
    && agent.runtime?.on_demand === true && agent.runtime.state !== "disabled";
}

export function localRuntimeApplied(agent: HubAgent): boolean {
  return typeof agent.revision === "number" && agent.local_runtime?.applied_revision === agent.revision;
}

export function instanceStatus(agent: HubAgent): {
  label: string;
  tone: "good" | "warning" | "neutral" | "critical";
} {
  if (agent.location === "local" && agent.client_id) {
    const runtime = agent.local_runtime, applied = localRuntimeApplied(agent);
    if (agent.desired_running === false && applied && runtime?.state === "stopped")
      return { label: "已停止", tone: "neutral" };
    if (applied && runtime?.state === "error")
      return { label: agent.desired_running === false ? "停止尚未确认" : "启动遇到问题", tone: "critical" };
    if (agent.client?.online === false)
      return { label: "等待 " + (agent.client.name || "电脑") + " 上线", tone: "warning" };
    if (agent.desired_running === false) return { label: "正在停止", tone: "warning" };
    if (agent.online) return { label: "已连接", tone: "good" };
    return { label: runtime?.state === "preparing" ? "正在准备" : "正在启动", tone: "warning" };
  }
  if (agent.location === "cloud" && agent.desired_running === false) {
    if (agent.runner?.last_error) return { label: "停止尚未确认", tone: "critical" };
    // A Hub with no supervisor says nothing of the process: a dropped socket alone does not prove it stopped.
    if (!agent.runner && !agent.online) return { label: "停止尚未确认", tone: "warning" };
    if (agent.online || agent.runner?.state !== "stopped") return { label: "正在停止", tone: "warning" };
    return { label: "已停止", tone: "neutral" };
  }
  if (agent.online) return { label: "已连接", tone: "good" };
  if (agent.location === "local" && !agent.paired)
    return { label: "等待配对", tone: "warning" };
  if (agent.runner?.last_error)
    return { label: "启动遇到问题", tone: "critical" };
  if (wakesOnDemand(agent)) {
    if (agent.runtime?.state === "sleeping") return { label: "休眠中", tone: "neutral" };
    if (agent.runtime?.state === "stopping") return { label: "正在休眠", tone: "neutral" };
    if (["starting", "restarting"].includes(agent.runtime!.state))
      return { label: "正在唤醒", tone: "warning" };
  }
  if (
    agent.runner?.state === "starting" ||
    agent.runner?.state === "restarting"
  )
    return { label: "正在启动", tone: "warning" };
  if (agent.runner?.state === "running")
    return { label: "等待连接", tone: "warning" };
  if (agent.location === "cloud" && agent.runner?.state === "stopped")
    return { label: "未运行", tone: "neutral" };
  return { label: "未连接", tone: "neutral" };
}
